import React, { useEffect, useState } from 'react'
import { Grid } from '@material-ui/core'
import { FormattedMessage } from 'react-intl'
import useFormikHelper from '../useFormikHelper'
import { useRouter } from 'next/router'
import DeleteModal from '@src/components/DeleteModal'
import { useSessionFieldsMutation } from '@src/mutations/ConfiguratorTubesMutation.generated'
import {
  ActiveLinkType,
  useProductFromQueryParam,
  useProductInput,
  checkForRedesignedBiTubeProduct,
} from '../ProductsConfiguration'
import { getSessionDataFromFormikData } from '../SessionDataUtil'
import GlobalAlert from '@src/components/GlobalAlert'
import SkeletonTableLoader from './TableSkeleton'
import { getConfirmOrderTableItemsByProduct } from './ConfirmOrderTableMap'
import { isNil } from 'lodash'
import ValidationWarnings from '@src/routes/Configurators/validations/ValidationWarnings'
import { TubeProperties } from '../../../utils/enum'
import { TubeProduct } from '../enum'
import StandardTable from './StandardTable'
import BiTubeTable from './BiTubeTable'
import useTubeTotalValidationPriceCall from '../useTubeTotalValidationPriceCall'

const ConfirmOrderTableRedesign = ({ loading }: { loading?: boolean }) => {
  const formik = useFormikHelper()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [idToBeDeleted, setIdToBeDeleted] = useState<number | null>(null)
  const [priceCallLoading, setPriceCallLoading] = useState(false)
  const router = useRouter()
  const product = useProductFromQueryParam()
  const isBiTubeProduct = checkForRedesignedBiTubeProduct(product)

  const { validateAndUpdatePrice } = useTubeTotalValidationPriceCall()

  const tableData = formik.allValues.oligoList

  useEffect(() => {
    const validateAndUpdate = async () => {
      setPriceCallLoading(true)
      await validateAndUpdatePrice()
      setPriceCallLoading(false)
    }
    validateAndUpdate()
  }, [])

  const hasError = !!formik.allValues.oligoList?.find(
    (val) => val?.listErrors?.length
  )

  const displayConcentrationColumn = tableData.some(
    (data) => data.format?.toLowerCase() !== 'dry'
  )

  const handleCloseIconClick = (id: number) => {
    if (isNil(id)) return
    setIdToBeDeleted(id)
    setDialogOpen(true)
  }

  const handleEditIconClick = (index: number) => {
    formik.setFieldValue('activeOligoIndex', index, false)
    router.replace(
      {
        pathname: router.pathname,
        query: {
          activeLink: ActiveLinkType.SequenceEntry,
          country: router.query.country,
          product: router.query.product,
          language: router.query.language,
        },
      },
      undefined,
      { shallow: true }
    )
  }

  const handleDeleteItem = async (index: number) => {
    const newList = formik.removeItemFromOligoList(index)
    setDialogOpen(false)
    setPriceCallLoading(true)
    await validateAndUpdatePrice(newList)
    setPriceCallLoading(false)
  }

  const [sessionFields] = useSessionFieldsMutation()
  const productInput = useProductInput()

  let confirmOrderTableItems = getConfirmOrderTableItemsByProduct(product)
  if (displayConcentrationColumn === false) {
    confirmOrderTableItems = confirmOrderTableItems.filter(
      (item) => item.id !== 'CONCENTRATION'
    )
  }

  useEffect(() => {
    const updateOligoListWithFlag = formik.allValues.oligoList.map((oligo) => {
      const computedFlag =
        product === TubeProduct.iscaledna &&
        ((formik.selectedOligoValues.purification === TubeProperties.VIVO &&
          oligo?.additionalServices?.length === 2) ||
          ((formik.selectedOligoValues.purification === TubeProperties.HPLC ||
            formik.selectedOligoValues.purification ===
              TubeProperties.DESALT) &&
            oligo?.additionalServices?.length === 1))
          ? 0
          : 1
      return { ...oligo, flag: computedFlag }
    })

    if (formik.allValues.oligoList?.[0]?.sequence !== '') {
      const sessionData = getSessionDataFromFormikData(
        product,
        updateOligoListWithFlag
      )
      sessionFields({
        variables: {
          input: sessionData,
          productInput,
        },
      })
    }
  }, [formik.allValues.oligoList])

  if (priceCallLoading || loading) return <SkeletonTableLoader />

  const warningMessages = [
    ...new Set(
      formik.allValues.oligoList
        .flatMap((item) => item?.listWarning || [])
        .filter((warning) => warning?.key === 'ReviewWarning')
        .map((warning) => warning.value.trim())
        .filter(Boolean)
    ),
  ]
  return (
    <Grid container spacing={6}>
      {hasError && (
        <Grid item xs={12}>
          <GlobalAlert severity="error">
            <FormattedMessage
              id="CONF_DNAPLATES_ERROR_STEP2"
              defaultMessage="Please correct the error(s) before proceeding to the next step."
            />
          </GlobalAlert>
        </Grid>
      )}
      {!hasError && (
        <Grid item xs={12}>
          {warningMessages && warningMessages.length > 0 && (
            <Grid container>
              <ValidationWarnings
                warnings={warningMessages}
                redesign
                fullWidth
              />
            </Grid>
          )}
        </Grid>
      )}
      <Grid item xs={12}>
        {isBiTubeProduct ? (
          <BiTubeTable
            tableData={tableData}
            handleCloseIconClick={handleCloseIconClick}
            handleEditIconClick={handleEditIconClick}
          />
        ) : (
          <StandardTable
            tableData={tableData}
            displayConcentrationColumn={displayConcentrationColumn}
            handleCloseIconClick={handleCloseIconClick}
            handleEditIconClick={handleEditIconClick}
            confirmOrderTableItems={confirmOrderTableItems}
          />
        )}
        <DeleteModal
          dialogOpen={dialogOpen}
          handleClose={() => setDialogOpen(false)}
          handleDelete={() =>
            !isNil(idToBeDeleted) && handleDeleteItem(idToBeDeleted)
          }
          deleteType="single"
        />
      </Grid>
    </Grid>
  )
}

export default ConfirmOrderTableRedesign
